import { useContext, useMemo, useEffect, useRef } from "react";
import { Outlet, useParams, Link, useLocation } from "react-router";
import { RefreshCwIcon } from "lucide-react";
import type { DayOfWeek, WeekPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { useWeekPlan, type WeekConflict } from "@/context/week-plan-context";
import { useAuth } from "@/context/auth-context";
import type { WeekOwner } from "@/lib/week-owner";
import { weekPath } from "@/lib/week-paths";
import { findRegimen, regimenBadge } from "@/data/meal-plan";
import { getMealChoices, isDayComplete } from "@/lib/random-plan";
import { getTodayDayOfWeek } from "@/lib/week-utils";
import { Button } from "@/components/ui/button";
import { ComprasToast, useComprasToast } from "@/components/ui/compras-toast";
import { EditedByBanner } from "@/components/meal-plan/edited-by-banner";
import { FriendModeContext } from "@/context/friend-mode-context";
import { useOnline } from "@/lib/use-online";

const VALID_DAYS = new Set<string>(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

const DAY_NAMES: Record<DayOfWeek, string> = {
  mon: "lunes",
  tue: "martes",
  wed: "miércoles",
  thu: "jueves",
  fri: "viernes",
  sat: "sábado",
  sun: "domingo",
};

/** "a", "a y b", "a, b y c". */
function joinList(items: string[]): string {
  return items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} y ${items.at(-1)}`;
}

/** "la cena", "el almuerzo", "la media tarde". */
function mealName(label: string): string {
  const name = label.toLowerCase();
  return `${/^(cena|media)/.test(name) ? "la" : "el"} ${name}`;
}

/**
 * What clashed, per day: "la cena del martes", "el almuerzo y la cena del
 * martes y el jueves" (a day without meal detail, e.g. a regimen change, is
 * the whole day).
 */
function formatConflict(conflict: WeekConflict, week: WeekPlan): string {
  return joinList(
    conflict.days.map((day) => {
      const regimen = findRegimen(week[day].regimenId);
      const meals = (conflict.meals?.[day] ?? [])
        .map((mi) => regimen?.meals[mi]?.label)
        .filter((label): label is string => Boolean(label));
      return meals.length > 0 ? `${joinList(meals.map(mealName))} del ${DAY_NAMES[day]}` : `el ${DAY_NAMES[day]}`;
    }),
  );
}

/**
 * The toast after another save won on some days. In my weeks the winner was
 * usually me on another device; in a friend's, usually the friend.
 */
function conflictMessage(
  conflict: WeekConflict,
  week: WeekPlan,
  owner: WeekOwner,
  myUserId: number | undefined,
): string {
  const days = formatConflict(conflict, week);
  const editor = conflict.editor;
  if (editor ? editor.id === myUserId : owner.kind === "self") {
    return `Cambiaste ${days} en otro dispositivo. Te mostramos esa versión; vuelve a hacer tu cambio si hace falta.`;
  }
  const name = editor?.username ?? owner.username;
  return `${name} también cambió ${days}. Te mostramos su versión; vuelve a hacer tu cambio si hace falta.`;
}

export function WeekLayout() {
  const { weekId, day } = useParams<{ weekId: string; day?: string }>();
  const { state, owner, navigateToWeek, retryLoad, ackConflict } = useWeekPlan();
  const { user } = useAuth();
  const myUserId = user?.id;
  const { toast, show: showToast, dismiss: dismissToast } = useComprasToast();

  // Load week data when URL weekId changes (wait for init to complete). A
  // failed load stays failed until Reintentar or another week.
  useEffect(() => {
    if (!weekId || !state.initialized || state.loading) return;
    if (state.currentWeekId === weekId && (state.weekLoaded || state.loadError)) return;
    void navigateToWeek(weekId);
  }, [weekId, state.initialized, state.loading, state.currentWeekId, state.weekLoaded, state.loadError, navigateToWeek]);

  // Another device's save won over my change on these days: say so once,
  // on that week (a conflict from a week no longer shown is dropped).
  const lastConflict = state.lastConflict;
  // Meal names come from the shown week (theirs, for the days that clashed).
  const weekPlanRef = useRef(state.weekPlan);
  weekPlanRef.current = state.weekPlan;
  const showingWeek = state.weekLoaded && state.currentWeekId === weekId ? weekId : null;
  useEffect(() => {
    if (!lastConflict) return;
    if (lastConflict.weekId !== showingWeek) {
      if (showingWeek) ackConflict();
      return;
    }
    showToast({
      message: conflictMessage(lastConflict, weekPlanRef.current, owner, myUserId),
      tone: "info",
      durationMs: 8000,
      // My change was dropped: say so at once.
      assertive: true,
    });
    ackConflict();
  }, [lastConflict, showingWeek, showToast, ackConflict, owner, myUserId]);

  // Determine active tab
  const location = useLocation();
  const isShopping = location.pathname.endsWith("/shopping");
  const isComidas = location.pathname.endsWith("/comidas");
  const activeDay = (day && VALID_DAYS.has(day) ? day : null) as DayOfWeek | null;

  if (state.loadError && !state.loading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-sm text-muted-foreground">
          {owner.kind === "friend" ? `No pudimos abrir el plan de ${owner.username}.` : "No pudimos abrir la semana."}
        </p>
        <Button variant="outline" size="sm" onClick={retryLoad}>
          <RefreshCwIcon className="size-4" /> Reintentar
        </Button>
      </div>
    );
  }

  if (state.loading || !weekId || !state.weekLoaded || state.currentWeekId !== weekId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ComprasToast toast={toast} onDismiss={dismissToast} />
      {/* Content. `min-h-full` on the inner wrapper lets children that opt
          into a sticky-bottom flex column (e.g. <ShoppingList />) pin their
          footer to the bottom edge of the scroll viewport, even when their
          content is shorter than the viewport. */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-full max-w-3xl flex-col px-4 py-4 gap-4">
          {/* "X actualizó tu semana": my weeks only, not in Compras. */}
          {owner.kind === "self" && !isShopping && <EditedByBanner key={weekId} />}
          <Outlet />
        </div>
      </div>

      {/* Macro summary (only for day view) */}
      {activeDay && !isShopping && !isComidas && <DayMacroSummary day={activeDay} />}

      {/* In friend mode the banner carries the save state. */}
      {owner.kind === "self" && <SaveStatusBar />}

      {/* Bottom navigation bar */}
      <DayBar
        weekId={weekId}
        activeDay={activeDay}
        isShopping={isShopping}
        isComidas={isComidas}
      />
    </div>
  );
}

// ── Day macro summary bar ──

function DayMacroSummary({ day }: { day: DayOfWeek }) {
  const { state } = useWeekPlan();
  const dayPlan = state.weekPlan[day];

  const totals = useMemo(() => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    if (!dayPlan.regimenId || !dayPlan.meals) return t;

    const regimen = findRegimen(dayPlan.regimenId);
    if (!regimen) return t;

    for (let mi = 0; mi < regimen.meals.length; mi++) {
      const meal = regimen.meals[mi]!;
      const mealSels = dayPlan.meals[mi];
      if (!mealSels) continue;
      const choices = getMealChoices(meal, dayPlan.mealMenus?.[mi]);

      for (let ci = 0; ci < choices.length; ci++) {
        const group = choices[ci]!;
        const cgSels = mealSels[ci];
        if (!cgSels || cgSels.skipped) continue;

        for (const sel of cgSels.selections) {
          const opt = group.options.find((o) => o.id === sel.optionId);
          if (!opt?.nutrition) continue;
          const p = opt.nutrition.perPortion;
          t.calories += p.calories * sel.count;
          t.protein += p.protein * sel.count;
          t.carbs += p.carbs * sel.count;
          t.fat += p.fat * sel.count;
        }
      }
    }

    t.calories = Math.round(t.calories);
    t.protein = Math.round(t.protein * 10) / 10;
    t.carbs = Math.round(t.carbs * 10) / 10;
    t.fat = Math.round(t.fat * 10) / 10;
    return t;
  }, [dayPlan]);

  if (!dayPlan.regimenId || totals.calories === 0) return null;

  return (
    <div className="border-t bg-background/95 backdrop-blur-sm">
      <div className="flex items-center justify-between px-4 py-2 mx-auto max-w-3xl text-xs tabular-nums">
        <div className="font-semibold">
          {totals.calories} <span className="text-muted-foreground font-normal">kcal</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-red-600">{totals.protein}<span className="text-muted-foreground ml-0.5">P</span></span>
          <span className="text-amber-600">{totals.carbs}<span className="text-muted-foreground ml-0.5">C</span></span>
          <span className="text-purple-600">{totals.fat}<span className="text-muted-foreground ml-0.5">G</span></span>
        </div>
      </div>
    </div>
  );
}

// ── Save status (self mode: failures only) ──

function SaveStatusBar() {
  const { state, retrySave } = useWeekPlan();
  const online = useOnline();
  if (state.saveStatus !== "error" && state.saveStatus !== "forbidden") return null;
  // Offline, queued saves retry by themselves when the connection is back.
  const offline = state.saveStatus === "error" && !online;

  return (
    <div
      role="alert"
      className="border-t border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-1.5 text-[13px] font-medium">
        <span className="min-w-0 flex-1">
          {offline
            ? "Sin conexión · se guardará al volver"
            : state.saveStatus === "error"
              ? "No se pudo guardar"
              : "No tienes permiso para guardar esta semana"}
        </span>
        {state.saveStatus === "error" && !offline && (
          <Button
            variant="ghost"
            size="sm"
            onClick={retrySave}
            className="-my-0.5 h-9 shrink-0 text-red-900 hover:bg-red-100 dark:text-red-100 dark:hover:bg-red-900/40"
          >
            <RefreshCwIcon className="size-4" /> Reintentar
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Bottom day bar ──

function DayBar({
  weekId,
  activeDay,
  isShopping,
  isComidas,
}: {
  weekId: string;
  activeDay: DayOfWeek | null;
  isShopping: boolean;
  isComidas: boolean;
}) {
  const { state, basePath, owner } = useWeekPlan();
  // Compras leaves friend mode: through the "¿Salir igual?" guard.
  const friendMode = useContext(FriendModeContext);
  const shoppingPath = weekPath(null, weekId, "shopping");

  function getDayStatus(day: DayOfWeek): "empty" | "partial" | "complete" {
    const dp = state.weekPlan[day];
    if (!dp.regimenId || !dp.meals) return "empty";
    const regimen = findRegimen(dp.regimenId);
    if (!regimen) return "empty";
    if (isDayComplete(regimen, dp.meals, dp.mealMenus)) return "complete";
    const hasAny = dp.meals.some((meal) =>
      meal.some((cg) => cg.selections.length > 0 || cg.skipped),
    );
    return hasAny ? "partial" : "empty";
  }

  const todayDay = getTodayDayOfWeek(state.currentWeekId);

  return (
    <div className="border-t bg-background safe-area-bottom">
      <div className="flex items-center gap-1.5 overflow-x-auto px-3 py-2 mx-auto max-w-3xl">
        <Link
          to={`${basePath}/${weekId}/comidas`}
          className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors mr-1 shrink-0 ${
            isComidas
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-muted"
          }`}
        >
          Comidas
        </Link>

        {DAYS_OF_WEEK.map(({ id, label }) => {
          const status = getDayStatus(id);
          const isActive = id === activeDay && !isShopping;
          const isToday = id === todayDay;
          const regimenId = state.weekPlan[id].regimenId;

          return (
            <Link
              key={id}
              to={`${basePath}/${weekId}/${id}`}
              className={`relative flex flex-col items-center gap-0.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors min-w-[3rem] shrink-0 ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              <span className={isToday ? `underline underline-offset-2 decoration-2 ${isActive ? "decoration-primary-foreground" : "decoration-primary"}` : ""}>{label}</span>
              {regimenId && (
                <span
                  className={`text-[10px] font-bold ${
                    isActive ? "text-primary-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {regimenBadge(regimenId)}
                </span>
              )}
              <span
                className={`absolute -top-0.5 -right-0.5 size-2 rounded-full ${
                  status === "complete"
                    ? "bg-green-500"
                    : status === "partial"
                      ? "bg-amber-500"
                      : "bg-transparent"
                }`}
              />
            </Link>
          );
        })}

        {/* Compras is always mine (my store and cart): from friend mode it
            leaves for my Compras of the same week. */}
        <Link
          to={shoppingPath}
          onClick={(e) => {
            if (owner.kind !== "friend" || !friendMode || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            friendMode.leaveTo(shoppingPath);
          }}
          className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ml-1 shrink-0 ${
            isShopping
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-muted"
          }`}
        >
          Compras
        </Link>
      </div>
    </div>
  );
}
